<template>
  <v-container fluid class="colaboradores-page py-6 px-4">
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card elevation="2">
          <v-card-title class="text-h5 font-weight-bold py-4 d-flex align-center flex-wrap">
            <v-icon left color="primary">mdi-account-group</v-icon>
            Colaboradores activos
            <v-spacer />
            <v-btn
              v-if="esAdmin"
              color="primary"
              small
              outlined
              @click="abrirAgregar"
            >
              <v-icon left small>mdi-account-plus</v-icon>
              Agregar colaborador
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <div v-if="loading" class="text-center py-12">
              <v-progress-circular indeterminate color="primary" size="48" />
              <p class="mt-3 text--secondary">Cargando...</p>
            </div>
            <v-simple-table v-else class="colaboradores-table">
              <thead>
                <tr>
                  <th class="text-left">Nombre</th>
                  <th class="text-left">Correo</th>
                  <th class="text-left">Área</th>
                  <th class="text-left">Cumpleaños</th>
                  <th class="text-left" style="width: 90px">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in colaboradoresActivos" :key="c.id">
                  <td class="font-weight-medium">{{ nombreCompleto(c) }}</td>
                  <td>
                    <a v-if="c.email_address" :href="`mailto:${c.email_address}`" class="text-decoration-none">
                      {{ c.email_address }}
                    </a>
                    <span v-else class="text--secondary">—</span>
                  </td>
                  <td>{{ c.area || "—" }}</td>
                  <td>{{ formatearCumpleanos(c.birthday) }}</td>
                  <td>
                    <v-btn
                      v-if="esAdmin"
                      icon
                      x-small
                      color="primary"
                      @click="abrirEditar(c)"
                      title="Modificar o eliminar"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <span v-else class="text--secondary">—</span>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <p v-if="!loading && colaboradoresActivos.length === 0" class="text-center text--secondary py-6">
              No hay colaboradores activos.
            </p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Diálogo Agregar / Modificar / Eliminar colaborador -->
    <v-dialog v-model="dialogEditar" max-width="600" persistent>
      <v-card>
        <v-card-title class="text-h6">{{ esModoEdicion ? "Modificar o eliminar colaborador" : "Agregar colaborador" }}</v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-text-field
            v-model="colaboradorSeleccionado.name"
            label="Nombre"
            outlined
            dense
            class="mb-2"
          />
          <v-text-field
            v-model="colaboradorSeleccionado.surname"
            label="Apellido"
            outlined
            dense
            class="mb-2"
          />
          <v-text-field
            v-model="colaboradorSeleccionado.email_address"
            label="Correo electrónico"
            type="email"
            outlined
            dense
            class="mb-2"
          />
          <v-text-field
            v-model="colaboradorSeleccionado.position"
            label="Cargo"
            outlined
            dense
            class="mb-2"
          />
          <v-text-field
            v-model="colaboradorSeleccionado.area"
            label="Área"
            outlined
            dense
            class="mb-2"
          />
          <v-text-field
            v-model="colaboradorSeleccionado.equipo"
            label="Equipo"
            outlined
            dense
            class="mb-2"
          />
          <v-menu
            v-model="menuCumpleanos"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="birthdayDisplay"
                label="Cumpleaños"
                outlined
                dense
                readonly
                v-bind="attrs"
                v-on="on"
                clearable
                placeholder="DD/MM/AAAA"
                class="mb-2"
                @click:clear="colaboradorSeleccionado.birthday = null"
              />
            </template>
            <v-date-picker
              v-model="colaboradorSeleccionado.birthday"
              no-title
              scrollable
              locale="es-cl"
              @input="menuCumpleanos = false"
            />
          </v-menu>
          <v-select
            v-model="colaboradorSeleccionado.status"
            :items="[
              { text: 'Activo', value: 'activo' },
              { text: 'Inactivo', value: 'inactivo' },
            ]"
            item-text="text"
            item-value="value"
            label="Estado"
            outlined
            dense
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-end">
          <v-btn text @click="cerrarDialogEditar">Cerrar</v-btn>
          <v-btn color="primary" :loading="guardando" @click="guardarColaborador">
            {{ esModoEdicion ? "Guardar" : "Agregar" }}
          </v-btn>
          <v-btn v-if="esModoEdicion" color="error" text @click="confirmarEliminacion">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo confirmar eliminación -->
    <v-dialog v-model="dialogEliminarConfirmado" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6">Confirmar eliminación</v-card-title>
        <v-card-text>
          ¿Estás seguro de que deseas eliminar al colaborador
          <strong>{{ colaboradorAEliminar ? nombreCompleto(colaboradorAEliminar) : "" }}</strong>?
          Se marcará como inactivo y dejará de aparecer en la lista de activos.
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="dialogEliminarConfirmado = false">Cancelar</v-btn>
          <v-btn color="error" text :loading="guardando" @click="eliminarColaborador">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor" top right>
      {{ snackbarMensaje }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import API_BASE from "@/helpers/api";

export default {
  name: "ColaboradoresView",
  data() {
    return {
      loading: true,
      colaboradores: [],
      dialogEditar: false,
      menuCumpleanos: false,
      guardando: false,
      colaboradorSeleccionado: {},
      dialogEliminarConfirmado: false,
      colaboradorAEliminar: null,
      snackbar: false,
      snackbarMensaje: "",
      snackbarColor: "success",
    };
  },
  computed: {
    esAdmin() {
      try {
        const u = JSON.parse(
          sessionStorage.getItem("usuario") || localStorage.getItem("usuario") || "null"
        );
        const rol = (u && (u.rol || u.role)) ? String(u.rol || u.role).trim().toLowerCase() : "";
        return rol === "admin" || rol === "adminti";
      } catch (e) {
        return false;
      }
    },
    colaboradoresActivos() {
      const activo = (this.colaboradores || []).filter(
        (c) => String(c.status || "").toLowerCase() === "activo"
      );
      return activo.sort((a, b) => nombreCompleto(a).localeCompare(nombreCompleto(b)));
    },
    birthdayDisplay() {
      const b = this.colaboradorSeleccionado && this.colaboradorSeleccionado.birthday;
      return b ? this.formatearCumpleanos(b) : "";
    },
    esModoEdicion() {
      return this.colaboradorSeleccionado && this.colaboradorSeleccionado.id != null;
    },
  },
  mounted() {
    this.cargar();
  },
  methods: {
    async cargar() {
      this.loading = true;
      try {
        const { data } = await axios.get(`${API_BASE}/colaboradores`);
        this.colaboradores = data || [];
      } catch (e) {
        console.error("Error al cargar colaboradores:", e);
        this.colaboradores = [];
        this.snackbarMensaje = "Error al cargar colaboradores.";
        this.snackbarColor = "error";
        this.snackbar = true;
      } finally {
        this.loading = false;
      }
    },
    nombreCompleto(c) {
      return [c.name, c.surname].filter(Boolean).join(" ") || "—";
    },
    formatearCumpleanos(val) {
      if (val == null || val === "") return "—";
      const str = typeof val === "string" ? val : String(val);
      const datePart = str.split("T")[0];
      if (!datePart || !/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return "—";
      const [y, m, d] = datePart.split("-").map(Number);
      return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
    },
    abrirAgregar() {
      this.colaboradorSeleccionado = {
        id: null,
        name: "",
        surname: "",
        email_address: "",
        position: "",
        area: "",
        equipo: "",
        backup_proyecto: "",
        birthday: null,
        status: "activo",
      };
      this.dialogEditar = true;
    },
    abrirEditar(c) {
      this.colaboradorSeleccionado = {
        id: c.id,
        name: c.name || "",
        surname: c.surname || "",
        email_address: c.email_address || "",
        position: c.position || "",
        area: c.area || "",
        equipo: c.equipo || "",
        backup_proyecto: c.backup_proyecto || "",
        birthday: (c.birthday && String(c.birthday).split("T")[0]) || null,
        status: String(c.status || "activo").toLowerCase(),
      };
      this.dialogEditar = true;
    },
    cerrarDialogEditar() {
      this.dialogEditar = false;
      this.colaboradorSeleccionado = {};
    },
    async guardarColaborador() {
      const c = this.colaboradorSeleccionado;
      if (!c) return;
      const payload = {
        name: (c.name || "").trim() || null,
        surname: (c.surname || "").trim() || null,
        email_address: (c.email_address || "").trim() || null,
        position: (c.position || "").trim() || null,
        area: (c.area || "").trim() || null,
        equipo: (c.equipo || "").trim() || null,
        backup_proyecto: (c.backup_proyecto || "").trim() || null,
        birthday: c.birthday || null,
        status: (c.status || "activo").toLowerCase(),
      };
      if (!payload.name) {
        this.snackbarMensaje = "El nombre es obligatorio.";
        this.snackbarColor = "error";
        this.snackbar = true;
        return;
      }
      this.guardando = true;
      try {
        if (c.id != null) {
          await axios.put(`${API_BASE}/colaboradores/${c.id}`, payload);
          this.snackbarMensaje = "Colaborador actualizado correctamente.";
        } else {
          await axios.post(`${API_BASE}/colaboradores`, payload);
          this.snackbarMensaje = "Colaborador agregado correctamente.";
        }
        this.cerrarDialogEditar();
        this.snackbarColor = "success";
        this.snackbar = true;
        await this.cargar();
      } catch (e) {
        console.error("Error al guardar:", e);
        this.snackbarMensaje = (e.response && e.response.data && e.response.data.error) || "Error al guardar.";
        this.snackbarColor = "error";
        this.snackbar = true;
      } finally {
        this.guardando = false;
      }
    },
    confirmarEliminacion() {
      this.colaboradorAEliminar = { ...this.colaboradorSeleccionado };
      this.dialogEliminarConfirmado = true;
    },
    async eliminarColaborador() {
      if (!this.colaboradorAEliminar || !this.colaboradorAEliminar.id) return;
      const id = this.colaboradorAEliminar.id;
      this.guardando = true;
      try {
        await axios.put(`${API_BASE}/colaboradores/${id}`, {
          name: this.colaboradorAEliminar.name,
          surname: this.colaboradorAEliminar.surname,
          email_address: this.colaboradorAEliminar.email_address,
          position: this.colaboradorAEliminar.position,
          area: this.colaboradorAEliminar.area,
          equipo: this.colaboradorAEliminar.equipo,
          backup_proyecto: this.colaboradorAEliminar.backup_proyecto,
          birthday: this.colaboradorAEliminar.birthday,
          status: "inactivo",
        });
        this.dialogEliminarConfirmado = false;
        this.dialogEditar = false;
        this.colaboradorAEliminar = null;
        this.cerrarDialogEditar();
        this.snackbarMensaje = "Colaborador marcado como inactivo.";
        this.snackbarColor = "success";
        this.snackbar = true;
        await this.cargar();
      } catch (e) {
        console.error("Error al eliminar:", e);
        this.snackbarMensaje = (e.response && e.response.data && e.response.data.error) || "Error al marcar como inactivo.";
        this.snackbarColor = "error";
        this.snackbar = true;
      } finally {
        this.guardando = false;
      }
    },
  },
};

function nombreCompleto(c) {
  return [c.name, c.surname].filter(Boolean).join(" ") || "—";
}
</script>

<style scoped>
.colaboradores-page {
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

.colaboradores-table >>> thead th {
  font-weight: 600;
  color: inherit;
}

.colaboradores-table >>> tbody tr:hover {
  background: rgba(0, 0, 0, 0.03);
}

.theme--dark .colaboradores-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.06);
}
</style>
